import { BehaviorSubject, combineLatest } from "rxjs";
import { Invitation, InvitationStatus } from "src/app/models/invitation";
import { NotificationCategory, UserNotification } from "src/app/models/userNotification";
import { map } from "rxjs/operators";
import * as moment from "moment";
import { Injectable } from "@angular/core";
import {InstructionStatus, Order} from "src/app/models/orders";
import { TradingFacades } from "../order/facades/trading.facades";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UserFacade } from "../user/facade/user.facade";
import { WithdrawalsService } from "src/app/services/http/withdrawals.service";
import {
    IRegularWithdrawalPlan,
    IRegularWithdrawalResponse,
    IWithdrawalInstruction,
    IWithdrawalInstructionResponse
} from "src/app/models/withdrawals";
import { BankAccountService } from "src/app/services/http/bank-account.service";
import { BankAccountStatus, IBankAccountViewModel } from "src/app/models/bank-account";
import { ClientEntity } from "src/app/models/clients";
import { GeneralSelectionFacade } from "../general-selection/facade/general-selection.facade";
import { BankAccountFacade } from "../bank-account/facade/bank-account.facade";
import {RegularWithdrawalsLookup} from "../../models/lookups/regularWithdrawalLookup";
import {WithdrawalFacade} from "../withdrawals/facade/withdrawal.facade";

@UntilDestroy()
@Injectable()
export class NotificationFacade {
    private _notifications$ = new BehaviorSubject<UserNotification[]>([]);
    public notifications$ = this._notifications$.asObservable();

    private _notificationsCount$ = new BehaviorSubject<number>(0);
    public notificationsCount$ = this._notificationsCount$.asObservable();

    private _invitationNotifications$ = new BehaviorSubject<UserNotification[]>([]);
    private userInvitationNotification$ = this._invitationNotifications$.asObservable();

    private _orderNotifications$ = new BehaviorSubject<UserNotification[]>([]);
    private orderNotifications$ = this._orderNotifications$.asObservable();

    private _withdrawalNotifications$ = new BehaviorSubject<UserNotification[]>([]);
    private withdrawalNotifications$ = this._withdrawalNotifications$.asObservable();

    private _bankAccountNotifications$ = new BehaviorSubject<UserNotification[]>([]);
    private bankAccountNotifications$ = this._bankAccountNotifications$.asObservable();


    private _regularWithdrawalNotifications$ = new BehaviorSubject<UserNotification[]>([]);
    private regularWithdrawalNotifications$ = this._regularWithdrawalNotifications$.asObservable();

    private selectedEntities: ClientEntity[] = [];

   constructor(
    private tradingsFacade: TradingFacades,
    private userFacade: UserFacade,
    private withdrawalFacade: WithdrawalFacade,
    private bankAccountService: BankAccountService, 
    private generalSelectionFacade: GeneralSelectionFacade,
    private bankAccountFacade: BankAccountFacade) { 
        this.subscribeToGeneralSelectionSelectedEntities(); 

        this.subscribeToOrderNotifications();
        this.subscribeToInvitationNotifications();
        this.subscribeToWithdrawalNotifications();
        this.subscriberToRegularWithdrawalNotifications();
        this.subscribeToBankAccountNotifications();
        this.loadNotifications();
    }

    private subscribeToGeneralSelectionSelectedEntities(): void {
        this.generalSelectionFacade.getSelectedInvestorEntities()
        .pipe(untilDestroyed(this))
        .subscribe((entities) => {
            this.selectedEntities = entities;
        });
    }

    /**
    * Combines together observables that emit notifications to create one combined stream of notifications.
    */
    private loadNotifications() : void {
        combineLatest([
            this.orderNotifications$,
            this.userInvitationNotification$,
            this.withdrawalNotifications$,
            this.bankAccountNotifications$,
            this.regularWithdrawalNotifications$,
        ])
        .pipe(untilDestroyed(this))
        .subscribe(([OrderNotifications, InvitationNotifications, WithdrawalNotifcations, bankAccountNotifications, regularWithdrawalsNotifications]) => {
            //Create a new array each time so we dont duplicate notifications.
            let notifications = OrderNotifications.concat(InvitationNotifications, WithdrawalNotifcations, bankAccountNotifications, regularWithdrawalsNotifications);

            this._notifications$.next(notifications);
            this._notificationsCount$.next(notifications.length);
        });
    }

    /**
    * Retrieves invitations and creates a notification for each invitation that is pending and not expired.
    */
    public subscribeToInvitationNotifications() : void { 
        this.userFacade.getUserInvitations$
        .pipe(map((invitations: Invitation[]) => {

            //If there are no invitations return an empty array
            if(!!!invitations || invitations.length === 0){
                return [];
            }
        
            let pendingInvitations = invitations.filter(x => x.status == InvitationStatus.Pending && !x.isExpired);

            //If there are no pending invitations also return an empty array
            if(!!!pendingInvitations || pendingInvitations.length === 0){return [];}

            return this.mapInvitationsAsNotifications(pendingInvitations);
            }), 
            untilDestroyed(this)
        )
        .subscribe(notifications => {
            this._invitationNotifications$.next(notifications);
        });
    }

    /**
     * Maps invitations as notification objects.
     */
    private mapInvitationsAsNotifications(invitations: Invitation[]) : UserNotification[] {
        return invitations.map(invitation => {
            return {
                notificationDescription: `You have a new invitation to access <b>${invitation.legalEntityName}</b> which expires ${moment(invitation.expiresAt).format("dddd MMMM Do YYYY")}.`,
                notificationDate: moment(invitation.createdAt).format("dddd MMMM Do YYYY"),
                notificationCategory: NotificationCategory.INVITATION,
                notificationLink: ['', { outlets: { 'sidebar': ['profile', 'invitations'] } }]
            } as UserNotification;
        });
    }

    /**
     * Retrieves orders and creates a notification for each order that requires approval.
     */
    public subscribeToOrderNotifications() : void {
        this.tradingsFacade.orders$.pipe(map((orders: Order[]) => {
            //If there are no notifications return an empty array
            if (!!!orders) { return [];}
    
            var ordersWithApprovalRequried = orders.filter(x => x.status === InstructionStatus.ApprovalRequired);

            if(!!!ordersWithApprovalRequried || ordersWithApprovalRequried.length === 0){ return [];}
    
            return this.mapOrdersAsNotifications(ordersWithApprovalRequried);
        }), 
        untilDestroyed(this))
        .subscribe(notifications => {
            this._orderNotifications$.next(notifications);
        });
    }

    /**
     * Maps orders as notification objects.
     */
    private mapOrdersAsNotifications(orders: Order[]) : UserNotification[] {
        return orders.map(order => {
            return {
              notificationDescription : this.getOrderDescription(order),
              notificationDate : moment(order.createdAt).format("dddd MMMM Do YYYY"),
              notificationCategory : NotificationCategory.ORDER,
              notificationLink: ['/','feature','client', 'buy-sell', 'pending-orders']
            } as UserNotification;
        });
    }
    
    /**
     * Generates a description for order notifications.
     */
    private getOrderDescription(order: Order) : string {
        return this.getOrderStatusText(order) + " for " + order.description + " of " + order.data.securityName;
    }
    
    /**
     * Returns the order status as a string.
     */
    private getOrderStatusText(order: Order) : string {
        switch (order.status) {
            case InstructionStatus.ReviewRequired:
                return "No Action Required";
            default:
                return order.statusName;
        }
    }

    /**
     * Retrieves withdrawal instructions and creates a notification for each withdrawal that requires approval.
    */
    public subscribeToWithdrawalNotifications() : void {
        this.withdrawalFacade.withdrawals$
        .pipe(map((withdrawalInstructionResponse: IWithdrawalInstructionResponse) => {

            if(!!withdrawalInstructionResponse.error && withdrawalInstructionResponse.error) { return [];}

            //If there are no withdrawal instructions return an empty array
            if (!!!withdrawalInstructionResponse.data) { return [];}

            let filteredWithdrawalInstructions = withdrawalInstructionResponse.data.filter(x => x.status === InstructionStatus.ApprovalRequired);

            if(filteredWithdrawalInstructions.length === 0) { return [];}

            return this.mapWithdrawalsAsNotifications(filteredWithdrawalInstructions);
    
        }), 
        untilDestroyed(this))
        .subscribe(notifications => {
            this._withdrawalNotifications$.next(notifications);
        });
    }
    
    public subscriberToRegularWithdrawalNotifications() : void {
        this.withdrawalFacade.regularWithdrawals$
            .pipe(map((regularWithdrawalInstructionResponse: IRegularWithdrawalResponse) => {
                if(!!regularWithdrawalInstructionResponse.error && regularWithdrawalInstructionResponse.error) { return [];}

                //If there are no regular withdrawal instructions return an empty array
                if (!!!regularWithdrawalInstructionResponse.data) { return [];}
                
                let filteredRegularWithdrawals = regularWithdrawalInstructionResponse.data.filter(w => w.instructionStatus == InstructionStatus.ApprovalRequired);
                
                if(filteredRegularWithdrawals.length == 0) { return []; }
                
                return this.mapRegularWithdrawalsAsNotifications(filteredRegularWithdrawals);
            }),
                untilDestroyed(this))
            .subscribe(notifications => {
                this._regularWithdrawalNotifications$.next(notifications);
            });   
    }

    /**
     * Maps withdrawal instructions as a notification.
     */
    private mapWithdrawalsAsNotifications(withdrawals: IWithdrawalInstruction[]) : UserNotification[] {
        let withdrawalsTab = 'withdrawals';
        return withdrawals.map(withdrawal => {
            return {
              notificationDescription : `You have a withdrawal of ${withdrawal.amount.value.toFixed(2)} ${withdrawal.amount.currency} requiring approval.`,
              notificationDate : moment(withdrawal.createdAt).format("dddd MMMM Do YYYY"),
              notificationCategory : NotificationCategory.WITHDRAWAL,
              notificationLink: ['/','feature','client', 'transactions'],
              queryParams: { tab: withdrawalsTab }
            } as UserNotification;
        });
    }
    
    /**
     * Maps regular withdrawal instructions as a notification.
     */
    private mapRegularWithdrawalsAsNotifications(regularWithdrawals: IRegularWithdrawalPlan[]) : UserNotification[] {
        let withdrawalsTab = 'withdrawals';
        return regularWithdrawals.map(withdrawal => {

            let period = RegularWithdrawalsLookup
                .find((x) => x.value == withdrawal.periodType)
                .key.toLowerCase();
            
            return {
                notificationDescription : `You have a regular withdrawal of ${withdrawal.amount.value.toFixed(2)} ${withdrawal.amount.currencyCode} ${period} from ${moment(withdrawal.startDate).format('DD/MM/YYYY')} requiring approval.`,
                notificationDate : moment(withdrawal.createdAt).format("dddd MMMM Do YYYY"),
                notificationCategory : NotificationCategory.WITHDRAWAL,
                notificationLink: ['/','feature','client', 'transactions'],
                queryParams: { tab: withdrawalsTab }
            } as UserNotification;
        });
    }
    

    /**
     * Combines observables containing investor bank accounts and a permission map detailing which investors the user has permissions to approve bank accounts for.
     * Maps bank accounts requiring approval into a notification format.
     */
    public subscribeToBankAccountNotifications() :void {
        combineLatest([
            this.bankAccountService.investorsBankAccountsResponse$,
            this.bankAccountFacade.bankAccountApprovalPermission$
        ])
        .pipe(untilDestroyed(this), map(([bankAccountResponse, permissionsMapResponse]) => {
            
            if(
                bankAccountResponse.error || permissionsMapResponse.error //Error retrieving data
                || bankAccountResponse.loading || permissionsMapResponse.loading //Some data still loading
                || !!!permissionsMapResponse.data == undefined   //Issue retrieving permissions
                || !!!bankAccountResponse.data || bankAccountResponse.data.length === 0  //No bank accounts
            )
            { 
                return [];
            }

            let filteredBankAccounts = bankAccountResponse.data.filter(x => x.status === BankAccountStatus.ApprovalRequired);

            if(filteredBankAccounts.length === 0) { return [];}

            let bankAccountApprovalPermissionsMap = permissionsMapResponse.data;

            filteredBankAccounts = filteredBankAccounts.filter(bankAccount => {
                let hasPermissionToApproveBankAccounts = bankAccountApprovalPermissionsMap.get(bankAccount.investorEntityId);
                if(hasPermissionToApproveBankAccounts === null || hasPermissionToApproveBankAccounts === undefined || !hasPermissionToApproveBankAccounts)
                    return false;

                let canApprove = this.bankAccountFacade.userCanApproveOrRejectGivenBankAccount(bankAccount);
                
                return hasPermissionToApproveBankAccounts && canApprove;
            })

            if(filteredBankAccounts.length === 0) { return [];}

            return this.mapBankAccountsAsNotifications(filteredBankAccounts);
        }))
        .subscribe(notifications => {
            this._bankAccountNotifications$.next(notifications);
        });
    }

    /**
     * Maps bank accounts requiring approval as a notification
     */
    private mapBankAccountsAsNotifications(bankAccounts: IBankAccountViewModel[]) : UserNotification[]{
        return bankAccounts.map(bankAccount => {
            return {
              notificationDescription : `<p>You have a new bank account for <b>${this.getEntityName(bankAccount.investorEntityId)}</b> that requires your approval.</p>`,
              notificationCategory : NotificationCategory.BANKACCOUNT,
              notificationLink: ['', { outlets: { 'sidebar': ['profile', 'bankAccounts'] }}]
            } as UserNotification;
        });
    }

    private getEntityName(entityId: number) : string {
        if(this.selectedEntities.length === 0)
            return;

        return this.selectedEntities.find(x => x.EntityId === entityId).Name;
    }
}